import type { Dispatch, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'

import { resourceTag } from '@krp/client-runtime'

import { newOperationId } from '../../../shared/model/operationIds'
import { normalizeRid } from '../../../shared/model/scopeKeys'
import type { PortOutcome, SuppliesPorts } from '../model/ports'
import { ONEC_SUPPLY_TAG, onecSupplyCommand, onecSupplyPostingStatus, suppliesApi } from './suppliesApi'

/**
 * Порты поставок по умолчанию: endpoints общего `baseApi`. Listener получает
 * dispatch стора приложения (с thunk middleware), поэтому запросы идут через
 * ЕДИНСТВЕННЫЙ transport и попадают в общий кэш.
 */
type ThunkCapable = ThunkDispatch<unknown, unknown, UnknownAction>

/** Пауза перед автосохранением правки (как у донора, `LINE_SAVE_DEBOUNCE_MS`). */
export const SUPPLY_SAVE_DEBOUNCE_MS = 500

async function settle<T, R>(started: { unwrap: () => Promise<T>; reset?: () => void }, map: (value: T) => R): Promise<PortOutcome<R>> {
  try {
    return { ok: true, value: map(await started.unwrap()) }
  } catch (error) {
    return { ok: false, error }
  } finally {
    // Итог команды — у операции слайса; запись мутации в кэше не нужна.
    started.reset?.()
  }
}

export function createSuppliesPorts(dispatch: Dispatch<UnknownAction>): SuppliesPorts {
  const run = dispatch as ThunkCapable
  return {
    newOperationId,
    debounceMs: SUPPLY_SAVE_DEBOUNCE_MS,
    send: (request) => settle(run(onecSupplyCommand.initiate(request)), (value) => value),
    readPostingStatus: (resourceId, supplyId) =>
      settle(
        run(onecSupplyPostingStatus.initiate({ resourceId, supplyId }, { forceRefetch: true, subscribe: false })),
        (value) => value.data,
      ),
    reloadSupply: (resourceId, supplyId) => {
      dispatch(suppliesApi.util.invalidateTags([resourceTag(ONEC_SUPPLY_TAG, normalizeRid(resourceId), supplyId.toLowerCase())]))
    },
  }
}
